import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NbDialogService, NbToastrService} from '@nebular/theme';
import {filter, map, share} from 'rxjs/operators';
import {
    DataPreparationService,
    ProcessStep,
    ProcessStepData,
    ProcessStepError,
    ValidatedFile,
} from '@core/interfaces/engin/data-preparation';
import {APIResponse} from '@core/interfaces/system/system-common';
import {LogDialogComponent} from '@theme/components/dialogs/log-dialog/log-dialog.component';
import {ConfirmDialogComponent} from '@theme/components';

export enum ActionButton {
    LOG = 'log',
    DELETE = 'delete',
}

@Component({
    selector: 'ngx-dp-file-validate',
    templateUrl: './file-validate.component.html',
    styleUrls: ['./file-validate.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileValidateComponent extends Unsubscribable implements OnChanges {
    @Input() processStep: ProcessStep;
    @Input() requiredFormFileType: string[];
    @Output() outputEvent = new EventEmitter();
    @Output() deleteFileEvent = new EventEmitter();

    fileUploadForm: FormGroup = this.fb.group({
        fileList: this.fb.array([], Validators.required),
    });

    title = 'Data File Validation';
    subTitle = 'All file must pass validation to proceed';
    tableColumns: any[] = [
        {
            code: 'fileName',
            title: 'File Name',
            width: '42%',
        },
        {
            code: 'fileType',
            title: 'File Type',
            width: '25%',
        },
        {
            code: 'validationStatus',
            title: 'Validation Status',
            width: '25%',
        },
        {
            code: 'action',
            title: '',
            width: '8%',
        },
    ];

    constructor(
        private fb: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private dialogService: NbDialogService,
        private dataPreparationService: DataPreparationService,
        private toastrService: NbToastrService,
    ) {
        super();
    }

    ngOnChanges(_: SimpleChanges): void {
        this.updateFileData();
    }

    onActionClick(data: {action: string; file: ValidatedFile}, index: number) {
        switch (data.action) {
            case ActionButton.DELETE:
                const confirmParam: any = {
                    title: `Delete File`,
                    message: `Are you sure you want to delete the file "${data.file.fileName}"?`,
                };

                this.dialogService
                    .open(ConfirmDialogComponent, {context: confirmParam})
                    .onClose.pipe(filter((res) => !!res.confirm))
                    .subscribe(() => this.removeFile(data.file, index));
                break;
            case ActionButton.LOG:
                this.dialogService.open(LogDialogComponent, {
                    context: {
                        fileNameToDownload: 'Step Error Log',
                        fileName: data.file?.fileName,
                        errorList$: this.dataPreparationService
                            .getStepIssueLog(this.processStep.id, data.file?.fileKey || null)
                            .pipe(
                                map((res: APIResponse<ProcessStepError[]>) => {
                                    return res.response.map((err: ProcessStepError) => ({
                                        errorMessage: err.stepErrorMessage,
                                        tableName: err.tableName,
                                        columnName: err.columnName,
                                        errorCount: err.errorCount,
                                    }));
                                }),
                                share(),
                            ),
                    },
                });
                break;
            default:
                break;
        }
    }

    removeFile(file, index) {
        // Delete file event will automatically refresh step data
        if (file) {
            this.deleteFileEvent.emit({stepId: this.processStep.id, fileKey: file.fileKey});
        }
    }

    get fileList() {
        return this.fileUploadForm.get('fileList') as FormArray;
    }

    triggerOutputEvent() {
        if (!this.checkRequiredTypesUploaded()) return;

        // "Next" call starts the data validation, so no need to check file status
        if (this.fileList.value.length > 0) {
            const failedFileStatus = this.fileList.value.filter(
                (file: ValidatedFile) => file.validationStatus === 'FAILED',
            );
            if (failedFileStatus.length === 0) {
                this.outputEvent.emit({validation: true, data: this.processStep.stepData});
            } else {
                this.outputEvent.emit({validation: false, data: this.processStep.stepData});
            }
        } else {
            this.outputEvent.emit({validation: false, data: this.processStep.stepData});
        }
    }

    private checkRequiredTypesUploaded(): boolean {
        const dataValue = this.fileList.value;
        const uploadedFiles = dataValue?.filter((item) => item.fileType !== '').map((item) => item.fileType);
        const requiredFormFiles = [];
        this.requiredFormFileType.map((type) => {
            if (uploadedFiles && uploadedFiles.length > 0 && !uploadedFiles.includes(type)) {
                requiredFormFiles.push(type);
            }
        });
        if (requiredFormFiles.length > 0) {
            this.outputEvent.emit({validation: false, data: this.processStep.stepData});
            this.showErrorMessage(requiredFormFiles);
            return false;
        } else {
            return true;
        }
    }

    private showErrorMessage(requiredFormFiles: string[]): void {
        const formattedNames = requiredFormFiles.map(
            (type) => `Form ${type.replace('FORM_', '').toLowerCase().replace(/s$/, '')}`,
        );

        const fileLabel = formattedNames.join(', ');
        const fileText = requiredFormFiles.length === 1 ? 'file' : 'files';
        const title = requiredFormFiles.length === 1 ? 'Missing required file.' : 'Missing required files.';

        this.toastrService.danger(`Please reset the process and upload the ${fileLabel} ${fileText}.`, title, {
            duration: 10000,
        });
    }

    private updateFileData() {
        const fileData: ProcessStepData[] = this.processStep.stepData.filter((d) => d.dataKey === 'OUTBOUND_FILES');

        if (fileData.length) {
            let validatedFiles: ValidatedFile[] = JSON.parse(fileData[0].dataValue) as ValidatedFile[];
            this.fileList.clear();
            validatedFiles.forEach((file) => {
                this.fileList.push(this.fb.group({...file}));
            });
            this.triggerOutputEvent();
        }
    }
}
